import { LockKeyhole } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { API } from "../data/siteData";
import { csrfHeaders } from "../utils/csrf";
import PortalDashboard from "../components/portal/PortalDashboard";
import PortalLogin from "../components/portal/PortalLogin";

async function fetchPortalData() {
  const profileResponse = await fetch(`${API}/auth/me`, { credentials: "include" });
  if (!profileResponse.ok) throw new Error();
  const profile = await profileResponse.json();
  const termsResponse = await fetch(`${API}/investor/terms`, { credentials: "include" });
  const terms = termsResponse.ok ? await termsResponse.json() : null;
  if (profile.access_status !== "approved" || profile.terms_accepted_version !== terms?.version) {
    return { profile, documents: [], renderings: [], terms };
  }
  const [documentsResponse, renderingsResponse] = await Promise.all([
    fetch(`${API}/investor/documents`, { credentials: "include" }),
    fetch(`${API}/investor/renderings`, { credentials: "include" }),
  ]);
  const documents = documentsResponse.ok ? await documentsResponse.json() : [];
  const renderings = renderingsResponse.ok ? await renderingsResponse.json() : [];
  return { profile, documents, renderings, terms };
}

export default function PortalPage() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [renderings, setRenderings] = useState([]);
  const [terms, setTerms] = useState(null);
  const [termsState, setTermsState] = useState("idle");
  const [state, setState] = useState("checking");
  const authError = useMemo(
    () => new URLSearchParams(location.search).get("auth_error") === "1" ? "We could not complete your secure sign-in. Please try again." : "",
    [location.search],
  );
  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/investor-portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const loadPortal = useCallback(async () => {
    try {
      const portalData = await fetchPortalData();
      setUser(portalData.profile);
      setDocuments(portalData.documents);
      setRenderings(portalData.renderings);
      setTerms(portalData.terms);
      setState("ready");
    } catch {
      setState("signed-out");
    }
  }, []);
  useEffect(() => { loadPortal(); }, [loadPortal]);
  const acceptTerms = async (version) => {
    setTermsState("loading");
    try {
      const response = await fetch(`${API}/investor/terms/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ version }),
      });
      if (!response.ok) throw new Error();
      setTermsState("idle");
      await loadPortal();
    } catch {
      setTermsState("error");
    }
  };
  const logout = async () => { await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }); setUser(null); setDocuments([]); setRenderings([]); setTerms(null); setState("signed-out"); };
  const download = async (fileRecord) => { const response = await fetch(`${API}/investor/documents/${fileRecord.document_id}/download`, { credentials: "include" }); if (!response.ok) return; const blob = await response.blob(); const url = URL.createObjectURL(blob); const anchor = window.document.createElement("a"); anchor.href = url; anchor.download = fileRecord.filename; anchor.click(); URL.revokeObjectURL(url); };
  if (state === "checking") return <main className="portal-state" data-testid="portal-loading"><LockKeyhole size={28}/><p>Verifying secure access…</p></main>;
  if (state === "signed-out") return <PortalLogin authError={authError} onSignIn={startLogin} />;
  return <PortalDashboard user={user} documents={documents} renderings={renderings} terms={terms} termsState={termsState} onAcceptTerms={acceptTerms} onDownload={download} onLogout={logout} />;
}